import { useMemo, useContext } from "react";
import UsageStatsCards from "./UsageStatsCards";
import UsageBarChartWrapper from "./UsageBarChartWrapper";
import { calcCost, transformForDotChart } from "../../utils/utils";
import { AppContext } from "../AppContext.tsx";


export default function UsageSummary({ usageData, selectedMonth, selectedYear }) {
  // Always call hooks at the top level!
  const { models } = useContext(AppContext);


  const effectivePerDayModel = usageData?.per_day_model ?? [];
  const effectiveSummary = usageData?.summary ?? {};

  // modelPricing can be safely blank until models exist
  const modelPricing = useMemo(() => {
    const pricingMap = {};
    if (models && Array.isArray(models)) {
      models.forEach((model) => {
        // Backend prices are already per token
        pricingMap[model.id] = {
          price: model.price || {
            input_tokens: 0,
            output_tokens: 0,
            reasoning_tokens: 0,
          },
          display_name: model.display_name,
        };
      });
    }
    return pricingMap;
  }, [models]);


  // Memoize allModels from available data and pricing
  const allModels = useMemo(() => {
    const set = new Set();
    effectivePerDayModel.forEach((day) =>
      day.models.forEach((m) => {
        const modelInfo = modelPricing[m.model_id];
        if (modelInfo) {
          set.add(modelInfo.display_name);
        }
      })
    );
    return Array.from(set);
  }, [effectivePerDayModel, modelPricing]);

  // Continue with all hooks before any conditional returns

  // Calculate spend and tokens by model
  let spendByModel = {};
  let tokenByModel = {};
  effectivePerDayModel.forEach((day) => {
    day.models.forEach((m) => {
      const modelInfo = modelPricing[m.model_id];
      if (modelInfo) {
        const pricing = modelInfo.price;
        const displayName = modelInfo.display_name;
        const totalCost = calcCost({
          inputTokens: m.input_tokens,
          outputTokens: m.output_tokens,
          reasoningTokens: m.reasoning_tokens || 0,
          inputPrice: pricing.input_tokens,
          outputPrice: pricing.output_tokens,
          reasoningPrice: pricing.reasoning_tokens,
        });
        spendByModel[displayName] = (spendByModel[displayName] || 0) + totalCost;
        tokenByModel[displayName] =
          (tokenByModel[displayName] || 0) +
          (m.input_tokens + m.output_tokens + (m.reasoning_tokens || 0));
      } else {
        // Model pricing not found - this can happen if models aren't loaded yet
        // The parent component should ensure models are loaded before rendering
      }
    });
  });

  const spendByModelArr = Object.entries(spendByModel).sort((a, b) => b[1] - a[1]);
  const tokenByModelArr = Object.entries(tokenByModel).sort((a, b) => b[1] - a[1]);
  const topModel = tokenByModelArr[0]?.[0] ?? "–";
  const top3 = tokenByModelArr.slice(0, 3);

  // Generate model colors dynamically using the new chart color palette
  const modelColors = useMemo(() => {
    const colors = {};
    const chartColors = [
      'var(--color-chart-1)',
      'var(--color-chart-2)', 
      'var(--color-chart-3)',
      'var(--color-chart-4)',
      'var(--color-chart-5)',
      'var(--color-chart-6)',
      'var(--color-chart-7)',
      'var(--color-chart-8)',
      'var(--color-chart-9)',
      'var(--color-chart-10)'
    ];
    
    // Assign colors to models in a consistent order
    allModels.forEach((model, index) => {
      colors[model] = chartColors[index % chartColors.length];
    });
    
    return colors;
  }, [allModels]);
  
  const accentSpend = "var(--color-primary)";
  const accentTokens = "var(--color-primary)";
  const accentModel = "var(--color-primary)";

  const totalTokens =
    effectiveSummary.total_tokens ||
    effectiveSummary.input_tokens +
      effectiveSummary.output_tokens +
      effectiveSummary.reasoning_tokens +
      (effectiveSummary.cached_input_tokens || 0) +
      (effectiveSummary.cached_output_tokens || 0);

  const tokenTable = [
    ["Input", effectiveSummary.input_tokens],
    ["Output", effectiveSummary.output_tokens],
    ["Reasoning", effectiveSummary.reasoning_tokens || 0],
    ...(effectiveSummary.cached_input_tokens || effectiveSummary.cached_output_tokens
      ? [
          ["Cached Input", effectiveSummary.cached_input_tokens || 0],
          ["Cached Output", effectiveSummary.cached_output_tokens || 0],
        ]
      : []),
  ].filter(([_, v]) => v && v > 0);

  const spendTotal = spendByModelArr.reduce((a, b) => a + b[1], 0);
  
  // Transform data for dot chart
  const dotChartData = useMemo(
    () => transformForDotChart(effectivePerDayModel, modelPricing),
    [effectivePerDayModel, modelPricing]
  );

  // Now do the defensive check after all hooks
  if (!usageData || !effectiveSummary) {
    return <div className="text-center text-secondary p-8">Loading usage data...</div>;
  }
  
  // Show a message if we have usage data but no models loaded yet
  if (effectivePerDayModel.length > 0 && (!models || models.length === 0)) {
    return <div className="text-center text-secondary p-8">Loading models...</div>;
  }
  
  // If no usage data, show empty state
  if (!effectivePerDayModel.length) {
    return <div className="text-center text-secondary p-8">No usage data available for this month.</div>;
  }

  return (
    <div className="w-full mx-auto">
      <UsageStatsCards
        spendByModelArr={spendByModelArr}
        spendTotal={spendTotal}
        tokenTable={tokenTable}
        totalTokens={totalTokens}
        top3={top3}
        topModel={topModel}
        tokenByModel={tokenByModel}
        spendByModel={spendByModel}
        modelColors={modelColors}
        accentSpend={accentSpend}
        accentTokens={accentTokens}
        accentModel={accentModel}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
      />
      
      <UsageBarChartWrapper data={dotChartData} />
    </div>
  );
}
